import { FC, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ReportViewer } from './ReportViewer';
import Button from 'jbc-front/components/Button';
import { Balloon } from 'jbc-front/components/presenters/ui/Balloon';
import { Edit } from 'jbc-front/components/icons';
import { ScrollSpy } from 'jbc-front/components/ScrollSpy/v2';
import { AnchorTarget } from '@/components/AnchorTarget';
import { RegularHealthCheckupReport } from '../../query';
import { Navi } from '../Navi';
import { EgovModal } from '../EgovModal';
import { Title } from './View/Title';
import { BasicInformation } from './View/BasicInformation';
import { Office } from './View/Office';
import { HealthCheckupInstitution } from './View/HealthCheckupInstitution';
import { SpecifiedWorkSummaries } from './View/SpecifiedWorkSummaries';
import { HealthCheckupSummaries } from './View/HealthCheckupSummaries';
import { Physician } from './View/Physician';
import { SubmitOn } from './View/SubmitOn';
import styles from './Report.scss';

interface ReportParams {
  report: RegularHealthCheckupReport;
}

export const Report: FC<ReportParams> = ({ report }) => {
  const [isReportPdfOpen, setIsReportPdfOpen] = useState(false);
  const [isInstitutionPdfOpen, setIsInstitutionPdfOpen] = useState(false);
  const [isBalloonOpen, setIsBalloonOpen] = useState(false);
  const location = useLocation();
  const editLink = `/regular_health_checkup/reports/${report.id}/edit`;

  return (
    <ScrollSpy location={location}>
      <div className={styles.row}>
        <div className={styles.sideColumn}>
          <Navi />
        </div>

        <div className={styles.mainColumn}>
          <div className={styles.actions}>
            <Button as={Link} to={editLink} icon={<Edit />} widthAuto>
              編集
            </Button>
            <div className={styles.rightActions}>
              <Balloon.Container onClose={() => setIsBalloonOpen(false)}>
                <Button onClick={() => setIsBalloonOpen(isOpen => !isOpen)} primary widthAuto>
                  PDF出力
                </Button>
                <Balloon isOpen={isBalloonOpen}>
                  <Balloon.Item onClick={() => setIsReportPdfOpen(isOpen => !isOpen)}>
                    定期健康診断結果報告書
                  </Balloon.Item>
                  <Balloon.Item onClick={() => setIsInstitutionPdfOpen(isOpen => !isOpen)}>
                    様式6別紙　健康診断実施機関
                  </Balloon.Item>
                </Balloon>
                {isReportPdfOpen &&
                  createPortal(
                    <ReportViewer
                      reportName={'regularHealthCheckupReportPdf'}
                      onClose={() => setIsReportPdfOpen(false)}
                    />,
                    document.body
                  )}
                {isInstitutionPdfOpen &&
                  createPortal(
                    <ReportViewer
                      reportName={'healthCheckupInstitutionPdf'}
                      onClose={() => setIsInstitutionPdfOpen(false)}
                    />,
                    document.body
                  )}
              </Balloon.Container>
              <EgovModal report={report} />
            </div>
          </div>

          <AnchorTarget name="title" />
          <Title report={report} editLink={editLink} />

          <AnchorTarget name="basic_information" />
          <BasicInformation report={report} editLink={editLink} />

          <AnchorTarget name="office" />
          <Office report={report} editLink={editLink} />

          <AnchorTarget name="health_checkup_institution" />
          <HealthCheckupInstitution report={report} editLink={editLink} />

          <AnchorTarget name="specified_work_summaries" />
          <SpecifiedWorkSummaries report={report} editLink={editLink} />

          <AnchorTarget name="health_checkup_summaries" />
          <HealthCheckupSummaries report={report} editLink={editLink} />

          <AnchorTarget name="physician" />
          <Physician report={report} editLink={editLink} />

          <AnchorTarget name="submit_on" />
          <SubmitOn report={report} editLink={editLink} />
        </div>
      </div>
    </ScrollSpy>
  );
};
